import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { QueryPagination, createQueryOptions } from '../../query';
import { RequestMeta } from '../../temporary/requestmeta.types';
import { useStoreApi } from '../api';
import { storeKeys } from '../query-key-factory';
import { createCauseQuery } from '../utils';
import { Cart, Carts } from './cart.types';
import { CartSearch } from './search.types';

type GetCartParams<T = Cart> = {
  cartId?: string;
  select?: (data: Cart) => T;
  causeId?: string;
  /** Which storeKey to use. Defaults to 'user'  */
  queryKeyId?: string;
  pendingCredential?: string;
};

const useGetCart = <T = Cart>() => {
  const StoreApi = useStoreApi();
  return async ({
    cartId,
    causeId,
    pendingCredential,
  }: Omit<GetCartParams<T>, 'select'>): Promise<Cart> => {
    const causeParam = causeId ? `cause_id=${causeId}` : undefined;
    const pendingCredentialParam = pendingCredential
      ? `pending_card_type=${pendingCredential}`
      : undefined;
    const paramString = [causeParam, pendingCredentialParam].filter(Boolean).join('&');
    const { data } = await StoreApi.get(
      `/carts/${cartId}?${paramString}&include=fee_groups,summary`
    );
    return data.cart;
  };
};

function useShowCart<T = Cart>({
  cartId,
  select,
  causeId,
  pendingCredential,
  queryKeyId = 'user',
}: GetCartParams<T>) {
  const getCart = useGetCart();
  const storeKeyString = storeKeys.id(queryKeyId).carts.id(cartId ?? '').detail;
  const key = [...storeKeyString] as unknown[];

  pendingCredential && key.push({ pendingCredential: pendingCredential });
  return useQuery({
    queryKey: key,
    queryFn: () => getCart({ cartId, causeId, pendingCredential }),
    enabled: !!cartId,
    select,
  });
}

const useGetCarts = () => {
  const StoreApi = useStoreApi();

  return async (context: { meta?: { queryString?: string } }) => {
    const { queryString } = context.meta || {};
    const { data } = await StoreApi.get<Carts>(`/carts?${queryString}`);
    return data;
  };
};

const useListCarts = ({
  causeId,
  pagination,
  queryOptions,
}: {
  causeId?: string;
  pagination?: QueryPagination;
  queryOptions?: CartSearch;
}) => {
  const getCarts = useGetCarts();
  const causeQuery = createCauseQuery(causeId);

  const { queryString, queryObject, queryPagination } = createQueryOptions<CartSearch>({
    search: queryOptions,
    pagination,
    existingSearchString: causeQuery,
  });

  const queryKey = causeId ? storeKeys.id(causeId).carts.all : [];

  return useQuery({
    queryKey: [...queryKey, queryObject, queryPagination],
    queryFn: getCarts,
    enabled: !!causeId,
    meta: { queryString, causeId },
    placeholderData: keepPreviousData,
  });
};

const useInfiniteListCarts = ({
  causeId,
  queryOptions,
}: {
  causeId?: string;
  queryOptions?: CartSearch;
}) => {
  const getCarts = useGetCarts();
  const causeQuery = createCauseQuery(causeId);
  const infiniteData = async ({ pageParam = { limit: 24, offset: 0 } }) => {
    const { queryString } = createQueryOptions<CartSearch>({
      search: queryOptions,
      pagination: {
        limit: pageParam.limit,
        offset: pageParam.offset,
      },
      existingSearchString: causeQuery,
    });
    const result = await getCarts({ meta: { queryString } });
    return result;
  };
  const queryKey = causeId ? storeKeys.id(causeId).carts.all : [];

  return useInfiniteQuery({
    queryKey: [...queryKey, queryOptions],
    queryFn: infiniteData,
    enabled: !!causeId,
    initialPageParam: { limit: 24, offset: 0 },
    getNextPageParam: (lastPage) => {
      if (!lastPage) return undefined;
      const { meta } = lastPage as { meta: RequestMeta };
      if (!meta) return undefined;
      const { has_more, limit, offset } = meta;
      if (!has_more) {
        return undefined;
      }
      return { limit, offset: offset + limit };
    },
  });
};
const useShowCartItem = ({ cartId, itemId }: { cartId?: string; itemId?: string }) => {
  return useShowCart({
    cartId,
    select: (data) => data?.cart_items.find((item) => item.uuid === itemId),
  });
};

const useShowCartItemStoreItem = (cartId?: string) => {
  return useShowCart({ cartId, select: (data) => data?.cart_items[0].store_item });
};

const useShowPendingCartFees = (cartId?: string, pendingCredential?: string) => {
  return useShowCart({ cartId, pendingCredential });
};

const usePrefetchShowCart = ({
  cartId,
  causeId,
  pendingCredential,
  queryKeyId = 'user',
}: GetCartParams) => {
  const getCart = useGetCart();
  const queryClient = useQueryClient();

  const storeKeyString = storeKeys.id(queryKeyId).carts.id(cartId ?? '').detail;
  const key = [...storeKeyString] as unknown[];
  const prefetch = () =>
    queryClient.prefetchQuery({
      queryKey: key,
      queryFn: () => getCart({ cartId, causeId, pendingCredential }),
    });
  return prefetch;
};

export {
  useGetCart,
  useGetCarts,
  useInfiniteListCarts,
  useListCarts,
  usePrefetchShowCart,
  useShowCart,
  useShowCartItem,
  useShowCartItemStoreItem,
  useShowPendingCartFees,
};
